<template>
  <aw-spring :class="$style.spring">
    <div :class="$style.catalogListPage">
      <aw-breadcrumb-version2 />

      <div :class="$style.catalogListPageWhiteBox">
        <lv-heading tag="h1" level="1" version2>
          {{ $awt('aw.cms.catalog.list.main_title') }}
        </lv-heading>

        <aw-separator margin-top="md" margin-bottom="md" />

        <div ref="hyperComponentEl">
          <aw-section-wrapper :has-padding="true">
            <template #content>
              <aw-tab-carousel
                :main-title="$awt('aw.cms.catalog.list.hyper_title')"
                :empty-placeholder="$awt('aw.cms.catalog.list.fallback_slide.text')"
                tab-key="id"
                slide-key="id"
                :tabs="tabs[STORE_TYPE_HYPER]"
                :breakpoints="breakpoints"
                :slides="slides[STORE_TYPE_HYPER] ?? {}"
                :is-carousel="false"
                flex-direction="column"
                @tab-opened="fetchCatalog(
                  {
                    storeType: STORE_TYPE_HYPER,
                    availability: $event.newTabItem.id,
                  },
                )"
              >
                <template #slide(all)(all)="{ slide }">
                  <div
                    :data-banner-id="slide.id"
                    :data-banner-index="getHyperGlobalBannerIndex(slide)"
                  >
                    <aw-catalog-card
                      :key="slide.id"
                      :catalog="slide"
                      :analytics-measurable="analyticsMeasurable"
                      :analytics-banner-interaction="extendHyperAnalyticsBannerInteraction(slide) ?? undefined"
                    />
                  </div>
                </template>
              </aw-tab-carousel>
            </template>
          </aw-section-wrapper>
        </div>

        <div ref="superComponentEl" :class="$style.catalogListPageSuperBlock">
          <aw-section-wrapper :has-padding="false">
            <template #content>
              <aw-tab-carousel
                :main-title="$awt('aw.cms.catalog.list.super_title')"
                :empty-placeholder="$awt('aw.cms.catalog.list.fallback_slide.text')"
                tab-key="id"
                slide-key="id"
                :tabs="tabs[STORE_TYPE_SUPER]"
                :breakpoints="breakpoints"
                :slides="slides[STORE_TYPE_SUPER] ?? {}"
                :is-carousel="false"
                flex-direction="column"
                @tab-opened="fetchCatalog(
                  {
                    storeType: STORE_TYPE_SUPER,
                    availability: $event.newTabItem.id,
                  },
                )"
              >
                <template #slide(all)(all)="{ slide }">
                  <div
                    :data-banner-id="slide.id"
                    :data-banner-index="getSuperGlobalBannerIndex(slide)"
                  >
                    <aw-catalog-card
                      :key="slide.id"
                      :catalog="slide"
                      :analytics-measurable="analyticsMeasurable"
                      :analytics-banner-interaction="extendSuperAnalyticsBannerInteraction(slide) ?? undefined"
                    />
                  </div>
                </template>
              </aw-tab-carousel>
            </template>
          </aw-section-wrapper>
        </div>
      </div>
    </div>
  </aw-spring>
</template>

<script setup lang="ts">
  import { computed, ref, nextTick, defineAsyncComponent } from 'vue';
  import { useNuxtApp, useAsyncData } from 'nuxt/app';
  import { defineI18nRoute } from '#i18n';
  import { AW_AX_CACHE_GLOBAL } from '~~/common/plugins/axios/awAxCache';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface.js';
  import { useBreadcrumbStore } from '~~/common/stores/breadcrumb.js';
  import { LvHeading } from '~~/common/components/loginet-vue-shop/index';
  import AwSpring from '~~/common/components/AwSpring.vue';
  import AwBreadcrumbVersion2 from '~~/common/components/Common/AwBreadcrumbVersion2.vue';
  import AwSeparator from '~~/common/components/Common/AwSeparator.vue';
  import { AnalyticsBannerInteraction } from '~~/common/utils/AnalyticsBannerInteraction';
  import { calculatePromotionPageHref, calculatePromotionCompanyNo } from '~~/common/utils/analyticsHelpers';
  import { useBannerInteractionAnalytics } from '~~/common/composables/useBannerInteractionAnalytics';
  import type { Schema } from '~~/common/types/api';

  type CatalogSlide = Partial<Schema<'CatalogModel'>> & {
    isPlaceholder?: boolean;
    campaignName?: string | null;
  };
  type TabAssociation<T> = Record<string, Record<string, T>>;

  const AwCatalogCard = defineAsyncComponent(() => import('~~/shop/app/components/Page/Catalog/AwCatalogCard.vue'));
  const AwTabCarousel = defineAsyncComponent(() => import('~~/common/components/AwTabCarousel.vue'));
  const AwSectionWrapper = defineAsyncComponent(() => import('~~/common/components/Page/Common/AwSectionWrapper.vue'));

  defineOptions({
    name: 'CatalogListPage',
  });
  // eslint-disable-next-line no-undef
  definePageMeta({
    layout: 'full-width',
  });
  false && /*#__PURE__*/ defineI18nRoute({
    paths: {
      en: '/catalog',
      hu: '/katalogusok',
    },
  });
  const {
    $awt,
    $api,
    $logger,
    $getHref,
    $getHost,
  } = useNuxtApp();
  const breadcrumbStore = useBreadcrumbStore();
  breadcrumbStore.setBreadcrumbs([
    {
      token: $awt('aw.common.category.home'),
      to: '/',
      disabled: false,
    },
    {
      token: $awt('aw.cms.catalog.list.breadcrumb'),
      to: 'catalog',
      disabled: true,
    },
  ]);
  const breakpoints = computed(() => {
    const sr = useUserInterfaceStore().mediaQueries;
    return [
      {
        at: sr['desktop-large-min'],
        count: 5,
      },
      {
        at: sr['desktop-medium-min'],
        count: 4,
      },
      {
        at: sr['tablet-min'],
        count: 3,
      },
      {
        at: true,
        count: 2,
      },
    ];
  });
  const STORE_TYPE_HYPER = 'hyper';
  const STORE_TYPE_SUPER = 'super';
  const tabs = computed(() => ({
    [STORE_TYPE_HYPER]: [
      {
        id: 'current',
        tabTitle: $awt('aw.cms.catalog.list.hyper.tabs.current'),
      },
      {
        id: 'next',
        tabTitle: $awt('aw.cms.catalog.list.hyper.tabs.next'),
      },
    ],
    [STORE_TYPE_SUPER]: [
      {
        id: 'current',
        tabTitle: $awt('aw.cms.catalog.list.super.tabs.current'),
      },
      {
        id: 'next',
        tabTitle: $awt('aw.cms.catalog.list.super.tabs.next'),
      },
    ],
  }));
  const analyticsMeasurable = true;
  const analyticsBase = AnalyticsBannerInteraction.create({
    promotionComponentId: 'katalogus',
    promotionCompanyNo: calculatePromotionCompanyNo() ?? null,
    promotionPageHref: calculatePromotionPageHref($getHref(), $getHost() ?? '') ?? '',
    promotionName: null,
    promotionBannerIndex: 1,
    promotionIndex: 1,
  });

  const hyperComponentEl = ref<HTMLElement | null>(null);
  const superComponentEl = ref<HTMLElement | null>(null);

  const cachedTabs: TabAssociation<Promise<CatalogSlide[]> | CatalogSlide[]> = {};
  const slides = ref<TabAssociation<CatalogSlide[]>>(createTabAssociation(() => Array<CatalogSlide>(1).fill({ isPlaceholder: true })));

  const hyperBannersRef = computed(() => {
    const allSlides = Object.values(slides.value[STORE_TYPE_HYPER] ?? {}).flat();
    return allSlides.map(catalog => ({
      ...catalog,
      id: String(catalog.id),
      campaignName: catalog.title || null,
    }));
  });

  const superBannersRef = computed(() => {
    const allSlides = Object.values(slides.value[STORE_TYPE_SUPER] ?? {}).flat();
    return allSlides.map(catalog => ({
      ...catalog,
      id: String(catalog.id),
      campaignName: catalog.title || null,
    }));
  });

  const superAnalyticsBase = {
    ...analyticsBase,
    promotionIndex: 2,
  };

  const {
    getGlobalBannerIndex: getHyperGlobalBannerIndex,
    extendAnalyticsBannerInteraction: extendHyperAnalyticsBannerInteraction,
    observeBannerCards: observeHyperBannerCards,
  } = useBannerInteractionAnalytics({
    componentEl: hyperComponentEl,
    banners: hyperBannersRef,
    analyticsBannerInteraction: analyticsBase,
    analyticsMeasurable,
  });

  const {
    getGlobalBannerIndex: getSuperGlobalBannerIndex,
    extendAnalyticsBannerInteraction: extendSuperAnalyticsBannerInteraction,
    observeBannerCards: observeSuperBannerCards,
  } = useBannerInteractionAnalytics({
    componentEl: superComponentEl,
    banners: superBannersRef,
    analyticsBannerInteraction: superAnalyticsBase,
    analyticsMeasurable,
  });
  const ssrData = createTabAssociation(({
    tabKey: storeType,
    id: availability,
  }: { tabKey: string; id: string }) => {
    return useAsyncData(
      `catalog__${storeType}__${availability}`,
      async () => {
        if (availability === 'current') {
          await fetchCatalog(
            {
              storeType,
              availability,
            },
          );
          // asyncdata return must be truthy
          return { cachedResult: cachedTabs[storeType]?.[availability] };
        } else {
          return {};
        }
      },
      {
        default: () => ({}),
      },
    );
  });
  if (import.meta.client) {
    for (const storeType in ssrData) {
      const ssrStore = ssrData[storeType];
      if (!ssrStore) {
        continue;
      }
      for (const availability in ssrStore) {
        const ssrEntry = ssrStore[availability];
        if (!ssrEntry) {
          continue;
        }
        const result = (await ssrEntry).data.value;
        if (!result || !('cachedResult' in result)) {
          break;
        }
        const arr = await result.cachedResult;
        if (!arr) {
          continue;
        }
        const storeCache = cachedTabs[storeType] ??= {};
        const slidesStore = slides.value[storeType] ??= {};
        if (!(availability in storeCache)) {
          storeCache[availability] = arr;
          slidesStore[availability] = arr;
        }
      }
    }
  }

  async function fetchCatalog (
    {
      storeType,
      availability,
    }: { storeType: string; availability: string },
  ) {
    try {
      const storeCache = cachedTabs[storeType] ??= {};
      if (availability in storeCache) {
        await storeCache[availability];
        return;
      }
      storeCache[availability] = (async function () {
        const arrayResult = await sendCatalogRequest({
          storeType,
          availability,
        });
        storeCache[availability] = arrayResult;
        return arrayResult;
      })();
      const slidesStore = slides.value[storeType] ??= {};
      slidesStore[availability] = await storeCache[availability];
      nextTick(() => {
        nextTick(() => {
          if (storeType === STORE_TYPE_HYPER) {
            observeHyperBannerCards();
          } else {
            observeSuperBannerCards();
          }
        });
      });
    } catch (error) {
      $logger.error(error);
    }
  }

  function createTabAssociation<T> (factoryFunc: (arg: { tabKey: string; id: string }) => T): TabAssociation<T> {
    return Object.entries(tabs.value).reduce<TabAssociation<T>>((acc, [tabKey, tabItems]) => {
      acc[tabKey] = (
        Object.fromEntries(
          tabItems.map(e => [
            e.id,
            factoryFunc({
              tabKey,
              id: e.id,
            }),
          ]),
        )
      );
      return acc;
    }, {});
  }

  async function sendCatalogRequest ({
    storeType,
    availability,
  }: { storeType: string; availability: string }): Promise<CatalogSlide[]> {
    try {
      const apiResult = await $api.$get<CatalogSlide[]>(`catalog/list?availability=${availability}&storeType=${storeType}`, { awAxCache: AW_AX_CACHE_GLOBAL });
      const arrayResult = (Array.isArray(apiResult)
        ? apiResult.filter(r => r) // BE can send array with null values (missing translations?)
        : []
      );
      return arrayResult;
    } catch (error) {
      $logger.error(error);
      return [];
    }
  }
</script>

<style module lang="scss" rel="stylesheet/scss">
.spring {
  @include mobile(max) {
    max-width: 100%;
  }
}

.catalogListPage {
  display: grid;
  gap: 20px;
  grid-template-columns: 1fr;

  @include tablet(min) {
    gap: 40px;
  }

  &WhiteBox {
    padding: 20px 16px;
    border-radius: 24px;
    background: white;

    @include tablet(min) {
      padding: 40px 24px;
      border-radius: 32px;
    }

    @include desktop-small(min) {
      padding: 40px;
    }
  }

  &SuperBlock {
    margin-top: 20px;
    @include tablet(min) {
      margin-top: 40px;
    }
  }
}
</style>
